import React from "react";
import { arrayOf, bool, func, object, oneOf, shape, string } from "prop-types";
import { useTheme } from "@mui/material/styles";
import { Box, Card, CardActionArea, CardContent, Typography } from "@mui/material";
import PlayIcon from "@mui/icons-material/PlayCircleFilled";
import { formateDate } from "../utils/date";

import PlatformChips from "./PlatformChips";
import ItemBadges from "./ItemBadges";
import { cardHover } from "../component-styles";
import googleAnalytics from "shared/analytics";

const VEIL_COLOR = "rgba(0, 0, 0, 0.35)";
const PLAY_ICON_COLOR = "rgba(255, 255, 255, .5)";

const VideoCard = ({
  video,
  handleClick,
  showDetails = true,
  showChips = true,
  isFeatured = false,
  variant = "vertical",
  sx = {},
}) => {
  const { palette, breakpoints, spacing } = useTheme();
  const { title, description, youtube_id, platform_tags = [] } = video;
  const handleCardClick = (event) => {
    event.currentTarget.blur();
    googleAnalytics.trackVideoThumbnailClick(title);
    handleClick(video);
  };
  const thumbnailUrl = `//img.youtube.com/vi/${youtube_id}/hqdefault.jpg`;
  const date = video.display_date ? formateDate(video.display_date) : null;

  const isHorizontal = variant === "horizontal";

  let playIconSize = isHorizontal ? 65 : 100;
  if (isFeatured) {
    playIconSize = 175;
  }

  const rootBase = {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
    border: `1px solid ${palette.secondary.light}`,
    flex: 1,
    "& .MuiCardContent-root": {
      flexDirection: "column",
    },
  };

  let rootStyles = {
    ...rootBase,
    borderRadius: 1,
    ...cardHover(palette),
    "&:hover": {
      "& div::before": {
        opacity: 0,
      },
    },
  };

  if (isFeatured) {
    rootStyles = {
      ...rootBase,
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      [breakpoints.up("lg")]: {
        borderRadius: 1,
      },
    };
  }

  if (isHorizontal) {
    rootStyles = {
      ...rootBase,
      "& .MuiCardContent-root": {
        minHeight: 75,
        flexDirection: "row",
        "& button": {
          minWidth: 150,
          flex: "1 1 150px",
        },
      },
    };
  }

  return (
    <>
      <Card sx={rootStyles} elevation={0}>
        <CardContent sx={{ display: "flex", flex: 1, padding: 0, "&:last-child": { padding: 0 } }}>
          <CardActionArea
            onClick={handleCardClick}
            aria-label="Video"
            sx={
              isHorizontal
                ? {
                    flex: "1 1 100px",
                    maxWidth: "100px",
                    "&:hover": {
                      "& > div::before": {
                        opacity: 0,
                      },
                    },
                  }
                : {
                    "&:hover,&:focus": {
                      "& > div::before": {
                        opacity: 0,
                      },
                      "& svg": {
                        opacity: 0.75,
                      },
                    },
                  }
            }
          >
            <Box
              style={{ backgroundImage: `url(${thumbnailUrl})` }}
              sx={{
                overflow: "hidden",
                height: 0,
                paddingTop: "56.25%",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                  background: VEIL_COLOR,
                  transition: ".2s opacity ease-in-out",
                },
                "& svg": {
                  fill: PLAY_ICON_COLOR,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                },
              }}
            >
              <PlayIcon style={{ fontSize: playIconSize }} />
            </Box>
            {showChips && <ItemBadges item={video} />}
          </CardActionArea>
          {showDetails && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: spacing(1),
                m: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {date && <Typography variant="overline">{date}</Typography>}
                <Typography
                  variant="h3"
                  component="a"
                  style={{
                    color: isHorizontal ? "black" : palette.secondary.main,
                    fontSize: sx.fontSize,
                  }}
                  onClick={handleCardClick}
                  sx={{
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "none",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                    },
                  }}
                >
                  {title}
                </Typography>
              </Box>
              {description && (
                <Typography variant="body1" sx={{ color: "black" }}>
                  {description}
                </Typography>
              )}
              {platform_tags.length > 0 && (
                <Box sx={{ position: "relative", alignSelf: "flex-start" }}>
                  <PlatformChips
                    chips={platform_tags}
                    style={{ marginTop: "auto", flexWrap: "wrap" }}
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

VideoCard.propTypes = {
  video: shape({
    title: string,
    description: string,
    youtube_id: string,
    display_date: string,
    platform_tags: arrayOf(string),
  }).isRequired,
  handleClick: func.isRequired,
  showDetails: bool,
  showChips: bool,
  isFeatured: bool,
  variant: oneOf(["vertical", "horizontal"]),
  sx: object,
};

export default VideoCard;
